<template>
  <div>
    <headline-container headline="Edit Competition" :breadcrumbs="breadcrumbs">
      <button type="button" class="btn btn-white pull-right" @click="destroy">
        <i class="fa fa-trash"></i> Delete
      </button>
    </headline-container>

    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Teams
            </h3>

            <p>
              This list displays the teams that participate in the competition.
            </p>

            <button
                type="button"
                class="btn btn-black btn-sm"
                data-toggle="modal"
                data-target="#create-competition-team-modal"
            >
              <i class="fa fa-plus"></i> Create Team
            </button>
          </help-text>
        </div>

        <div class="col-xs-18">
          <CompetitionTeamList :competition="competition"/>
        </div>
      </div>

      <hr class="panel-separator">

      <form @submit.prevent="submit">
        <div class="row">
          <div class="col-xs-6">
            <help-text>
              <h3>
                Edit Competition
              </h3>

              <p>
                Enter the details of the competition.
              </p>
            </help-text>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body">
                <TextFormGroup
                    v-model="data.name"
                    name="name"
                    label="Name"
                    help-text="The name of the competition."
                    :error="getError('name')"
                />

                <CkEditorFormGroup
                    v-model="data.description"
                    name="description"
                    label="Description"
                    help-text="The description of the competition."
                    :error="getError('description')"
                />

                <TextFormGroup
                    v-model="data.shared_target"
                    name="shared_target"
                    label="Shared Target"
                    help-text="A shared target that all teams in the competition contribute to."
                    :error="getError('shared_target')"
                />

                <DateFormGroup
                    v-model="data.starts_at"
                    name="starts_at"
                    label="Starts At"
                    help-text="The start date of the competition."
                    :error="getError('starts_at')"
                />

                <DateFormGroup
                    v-model="data.ends_at"
                    name="ends_at"
                    label="Ends At"
                    help-text="The end date of the competition."
                    :error="getError('ends_at')"
                />

                <RadioFormGroup
                    v-model="data.is_active"
                    name="is_active"
                    label="Active"
                    :error="getError('is_active')"
                    :options="[{value: true, label:'Yes'}, {value: false, label:'No'}]"
                />

                <RepeatableFormGroup
                    label="Prizes"
                    v-model="data.prizes"
                    help-text="The prizes that are awarded for the competition."
                    :errors="prizeErrors"
                >
                  <template v-slot:default="{ data, index, change, errors }">
                    <input
                        type="text"
                        class="form-control"
                        :value="data.text"
                        @input="change(index, 'text', $event.target.value)"
                    >
                  </template>
                </RepeatableFormGroup>
              </div>
            </div>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-6">
            <help-text>
              <h3>
                Driver Settings
              </h3>

              <p>
                Adjust the driver specific settings for the competition.
              </p>
            </help-text>
          </div>

          <div class="col-xs-18">
            <div class="panel panel-default">
              <div class="panel-body">
                <base-form
                    v-model="data.driver_parameters"
                    :driver="data.driver"
                    :errors="driverErrors"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-offset-6 col-xs-18">
            <button type="submit" class="btn btn-block btn-black tw-mb-8">
              <i class="fa fa-save"></i> Save
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import BaseForm from '@/components/Administration/Competitions/DriverParameterForms/Base'
import BasicList from '@/components/Panels/Lists/Basic'
import CkEditorFormGroup from '@/vendor/forms/FormGroups/Block/CkEditor'
import CompetitionTeamList from '../../../components/Administration/CompetitionTeams/Panels/List'
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import Errors from '@/mixins/Errors'
import FormGroup from '@/vendor/forms/FormGroups/Block/FormGroup'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Block/Radio'
import RepeatableFormGroup from '../../../components/Forms/RepeatableFormGroup'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Block/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'

export default {
  components: {
    BaseForm,
    BasicList,
    CkEditorFormGroup,
    CompetitionTeamList,
    DateFormGroup,
    FormGroup,
    HeadlineContainer,
    HelpText,
    LoadingContainer,
    RadioFormGroup,
    RepeatableFormGroup,
    SubmitFormGroup,
    TextFormGroup
  },

  mixins: [Errors],

  props: ['competitionId'],

  data() {
    return {
      competition: null,
      data: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    breadcrumbs() {
      return [
        {url: '/competitions', text: 'Competitions'},
        {text: 'Edit'}
      ]
    },

    driverErrors() {
      return Object.entries(this.errors)
          .filter(entry => entry[0].startsWith('driver_parameters.'))
          .reduce((result, entry) => ({
            ...result,
            [entry[0].substring('driver_parameters.'.length)]: entry[1]
          }), {})
    },

    prizeErrors() {
      return Object.entries(this.errors)
          .filter(entry => entry[0].startsWith('prizes.'))
          .reduce((result, entry) => ({
            ...result,
            [entry[0].substring('prizes.'.length)]: entry[1]
          }), {})
    }
  },

  methods: {
    load() {
      http.get('/api/v2/competitions/' + this.competitionId).then(response => {
        this.competition = response.data.data

        this.data = response.data.data.attributes

        this.loading = false
      })
    },

    submit() {
      http.put('/api/v2/competitions/' + this.competitionId, this.data)
          .then(response => {
            this.competition = response.data.data

            notify.success('The competition was saved')
          })
          .catch(this.catchValidationErrors)
    },

    destroy() {
      modal.confirm('Are you sure that you want to delete the competition?', () => {
        http.delete('/api/v2/competitions/' + this.competitionId)
            .then(() => {
              notify.success('The competition was deleted')

              this.$router.push('/competitions')
            })
      })
    }
  }
}
</script>
